import { Alert, Button, Empty, Input, Table } from 'antd'
import { observer } from 'mobx-react-lite'
import { TbDownload, TbLock } from 'react-icons/tb'

import { formatDateTimeUk } from '@/Ship/Helpers/fieldViews'
import { classNames } from '@/Ship/Helpers/classNames'

import { archiveEntryKind } from '../Helpers/diskArchive'
import { formatBytes, pluralUk } from '../Helpers/diskFormat'
import { fileViewerStore as store } from '../Stores/FileViewerStore'
import { DiskNodeIcon } from './DiskNodeIcon'

import type { DiskArchiveRow } from '../Helpers/diskArchive'
import type { TableProps } from 'antd'

import styles from './DiskFileViewer.module.css'

/** Більше рядків верхнього рівня — сторінками: тисячі рядків таблиці гальмують шар. */
const PAGE_SIZE = 200

const filesLabel = (count: number): string => `${count} ${pluralUk(count, 'файл', 'файли', 'файлів')}`
const foldersLabel = (count: number): string => `${count} ${pluralUk(count, 'папка', 'папки', 'папок')}`
const entriesLabel = (count: number): string => `${count} ${pluralUk(count, 'запис', 'записи', 'записів')}`

/**
 * Режим «архів» переглядача: вміст ZIP без розпакування — дерево папок
 * (розгортаються), розмір і стиснутий розмір, дата з архіву; зверху — скільки
 * файлів і папок, скільки займе розпаковане, пошук за назвою запису (пласкі
 * збіги з папкою) і «Скачати архів».
 *
 * Бек віддає не більше `limit` записів; якщо архів більший, перелік це
 * називає — підсумки при цьому по всьому архіву.
 */
export const DiskArchiveView = observer(({ compact, onDownload }: { compact: boolean; onDownload: () => void }) => {
    const archive = store.archive
    const listing = archive.listing

    if (listing === null) return null

    const { total } = listing
    const searching = archive.isSearching
    const rows = archive.rows

    const columns: NonNullable<TableProps<DiskArchiveRow>['columns']> = [
        {
            key: 'name',
            title: 'Назва',
            render: (_: unknown, row) => (
                <span className={classNames(styles['archiveName'])}>
                    <DiskNodeIcon kind={archiveEntryKind(row)} size={22} />
                    <span className={classNames(styles['archiveNameText'])}>
                        <span className={classNames(styles['archiveTitle'])} title={row.key}>
                            {row.name}
                        </span>
                        {searching && row.folder !== '' && (
                            <span className={classNames(styles['archiveSub'])} title={row.folder}>
                                {row.folder}
                            </span>
                        )}
                        {compact && (
                            <span className={classNames(styles['archiveSub'])}>
                                {row.dir ? filesLabel(row.files) : formatBytes(row.size)}
                            </span>
                        )}
                    </span>
                    {row.encrypted && (
                        <TbLock className={classNames(styles['archiveLock'])} aria-label="Зашифровано паролем" />
                    )}
                </span>
            ),
        },
        {
            key: 'size',
            title: 'Розмір',
            width: 120,
            align: 'right',
            render: (_: unknown, row) => (
                <span className={classNames(styles['archiveMuted'])} title={row.dir ? filesLabel(row.files) : undefined}>
                    {formatBytes(row.size)}
                </span>
            ),
        },
        {
            key: 'compressed',
            title: 'Стиснуто',
            width: 120,
            align: 'right',
            render: (_: unknown, row) => (
                <span className={classNames(styles['archiveMuted'])}>{formatBytes(row.compressedSize)}</span>
            ),
        },
        {
            key: 'modifiedAt',
            title: 'Змінено',
            width: 170,
            render: (_: unknown, row) => (
                <span className={classNames(styles['archiveMuted'])}>{formatDateTimeUk(row.modifiedAt)}</span>
            ),
        },
    ]

    return (
        <div className={classNames(styles['archive'])} data-testid="disk-viewer-archive">
            <div className={classNames(styles['archiveBar'])}>
                <div className={classNames(styles['archiveSummary'])} data-testid="disk-viewer-archive-summary">
                    {filesLabel(total.files)} · {foldersLabel(total.folders)} · розпаковано {formatBytes(total.size)}
                    <span className={classNames(styles['archiveMuted'])}> · архів {formatBytes(listing.archiveSize)}</span>
                </div>
                <Input.Search
                    className={classNames(styles['archiveSearch'])}
                    placeholder="Пошук в архіві"
                    allowClear
                    value={archive.query}
                    onChange={(event) => archive.setQuery(event.target.value)}
                    onSearch={(value) => archive.setQuery(value)}
                    data-testid="disk-viewer-archive-search"
                />
                <Button icon={<TbDownload />} onClick={onDownload} data-testid="disk-viewer-archive-download">
                    Скачати архів
                </Button>
            </div>
            {listing.hidden > 0 && (
                <Alert
                    className={classNames(styles['archiveNotice'])}
                    type="info"
                    showIcon
                    title={`Показано перші ${entriesLabel(listing.shown)} з ${listing.shown + listing.hidden}: решту видно у скачаному архіві. Підсумки вгорі — по всьому архіву.`}
                    data-testid="disk-viewer-archive-hidden"
                />
            )}
            {total.entries === 0 ? (
                <div className={classNames(styles['center'])}>
                    <Empty description="Архів порожній" />
                </div>
            ) : (
                <div className={classNames(styles['archiveScroll'])}>
                    <Table<DiskArchiveRow>
                        size="small"
                        rowKey="key"
                        columns={compact ? columns.filter((column) => column.key === 'name') : columns}
                        dataSource={rows}
                        showHeader={!compact}
                        tableLayout="fixed"
                        pagination={
                            rows.length > PAGE_SIZE
                                ? { pageSize: PAGE_SIZE, showSizeChanger: false, size: 'small', hideOnSinglePage: true }
                                : false
                        }
                        locale={{ emptyText: searching ? 'Нічого не знайдено' : ' ' }}
                        data-testid="disk-viewer-archive-table"
                    />
                </div>
            )}
        </div>
    )
})
